export const applyTravelTemplate = (html, pages = [], userName = "", bannerImage = "", footerImage = "") => {
  let processedHtml = html || "";
  if (userName) {
    processedHtml = processedHtml.replace(/{%user_name%}/g, userName);
  }
  const fallbackImage = "https://open-domo-portals.s3.us-west-2.amazonaws.com/form_file-2f036911hmquzauxe-0";
  const resolvedBanner = bannerImage || fallbackImage;
  processedHtml = processedHtml.replace(/{%banner_image%}/g, resolvedBanner);
  const resolvedFooter = footerImage || fallbackImage;
  processedHtml = processedHtml.replace(/{%footer_image%}/g, resolvedFooter);

  const parser = new DOMParser();
  const doc = parser.parseFromString(processedHtml, "text/html");

  const list = Array.isArray(pages) ? pages : [];

  const resolveImage = (img) => {
    if (!img) return "";
    const trimmed = img.trim();
    // {%page_image%} is substituted directly into a `src="{%page_image%}"`
    // attribute, so this must always resolve to a plain URL-safe string —
    // never raw markup, or the quotes inside it break the attribute.
    if (trimmed.startsWith("<svg")) return `data:image/svg+xml,${encodeURIComponent(trimmed)}`;
    return trimmed;
  };

  const setPageAttributes = (node, page) => {
    if (!node || !page) return;
    if (page?.slug) node.setAttribute("data-page-slug", page.slug);
    if (page?.page_type) node.setAttribute("data-page-type", page.page_type);
    if (page?.page_link) node.setAttribute("data-page-link", page.page_link);
    if (page?.page_link_newtab != null) node.setAttribute("data-page-link-newtab", page.page_link_newtab ? "true" : "false");
  };

  list.forEach((page) => {
    const injectVars = (str) =>
      (str || "")
        .replace(/{%page_name%}/g, page?.name || "Unnamed Page")
        .replace(/{%page_image%}/g, resolveImage(page?.image))
        .replace(/{%page_type%}/g, page?.page_type || "")
        .replace(/{%page_link%}/g, page?.page_link || "")
        .replace(/{%page_link_newtab%}/g, page?.page_link_newtab ? "true" : "false")
        .replace(/{%page_slug%}/g, page?.slug || "")
        .replace(/{%user_name%}/g, userName)
        .replace(/{%banner_image%}/g, resolvedBanner)
        .replace(/{%footer_image%}/g, resolvedFooter);

    const idNode = page?._id ? doc.querySelector(`[data-page-id="${page._id}"]`) : null;
    if (idNode) {
      idNode.innerHTML = injectVars(idNode.innerHTML);
      setPageAttributes(idNode, page);
      idNode.removeAttribute("data-page-id");
    }

    const node = doc.getElementById(page.slug);
    if (node) {
      node.innerHTML = injectVars(node.innerHTML) || page?.name || "Unnamed Page";
      setPageAttributes(node, page);
    }

    const proto = doc.getElementById("page_item");
    if (proto && proto.parentElement) {
      const clone = proto.cloneNode(true);
      clone.removeAttribute("id");
      clone.innerHTML = injectVars(clone.innerHTML) || page?.name || "Unnamed Page";
      setPageAttributes(clone, page);
      proto.parentElement.appendChild(clone);
    }
  });

  const proto = doc.getElementById("page_item");
  if (proto && proto.parentElement) proto.remove();

  // Remove any elements that still have data-page-id (since active ones had it removed)
  doc.querySelectorAll("[data-page-id]").forEach((node) => {
    node.remove();
  });

  // Swap the placeholder <img src="data:image/svg+xml,..."> back into a real
  // inline <svg>, so icons inherit currentColor/text color from the tile
  // instead of being locked to whatever color was baked into the markup.
  doc.querySelectorAll('img[src^="data:image/svg+xml,"]').forEach((imgEl) => {
    const encoded = imgEl.getAttribute("src").slice("data:image/svg+xml,".length);
    let svgMarkup;
    try {
      svgMarkup = decodeURIComponent(encoded);
    } catch {
      return;
    }
    const svgEl = parser.parseFromString(svgMarkup, "image/svg+xml").documentElement;
    if (!svgEl || svgEl.nodeName.toLowerCase() !== "svg") return;
    if (imgEl.className) svgEl.setAttribute("class", imgEl.className);
    const style = imgEl.getAttribute("style") || "";
    svgEl.setAttribute("style", `${style}${style && !style.trim().endsWith(";") ? ";" : ""}color:#fff;`);
    imgEl.replaceWith(doc.importNode(svgEl, true));
  });

  return "<!doctype html>\n" + doc.documentElement.outerHTML;
};

export const applyListTemplate = (html, pageName = "Documents", docs = []) => {
  const tplWithPage = (html || "").replace(/{%page_name%}/g, pageName);

  const parser = new DOMParser();
  const doc = parser.parseFromString(tplWithPage, "text/html");

  const list = Array.isArray(docs) ? docs : [];

  list.forEach((d) => {
    let node = doc.getElementById(d.slug);

    if (node) {
      // Found by slug → replace contents in place
      node.innerHTML = (node.innerHTML || "").replace(/{%document_name%}/g, d?.name || "Unnamed Document") || d?.name || "Unnamed Document";
      if (d?._id) node.setAttribute("data-doc-id", d._id);
    }

    // Fall back to the "document_item" prototype (all documents in the loop)
    const proto = doc.getElementById("document_item");
    if (proto && proto.parentElement) {
      const clone = proto.cloneNode(true);
      clone.removeAttribute("id");
      clone.innerHTML = (clone.innerHTML || "").replace(/{%document_name%}/g, d?.name || "Unnamed Document") || d?.name || "Unnamed Document";
      if (d?._id) clone.setAttribute("data-doc-id", d._id);
      proto.parentElement.appendChild(clone);
    }
  });

  const proto = doc.getElementById("document_item");
  if (proto && proto.parentElement) proto.remove();

  return "<!doctype html>\n" + doc.documentElement.outerHTML;
};

export const applyMenuTemplate = (html, menus = [], currentUrl = "") => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");

  const menuProto = doc.getElementById("menu_item");
  const sidebar = doc.getElementById("sidebar");

  if (!menuProto || !sidebar) return html;

  const normalize = (url) => (typeof url === "string" ? url.replace(/\/$/, "") : "");
  const current = normalize(currentUrl);

  const cloneProto = () => {
    const node = menuProto.cloneNode(true);
    node.removeAttribute("id");
    return node;
  };

  const injectIcon = (node, image) => {
    if (!image) return;
    const iconSpan = doc.createElement("span");
    iconSpan.innerHTML = image.includes("svg") ? image : `<img src="${image}" alt="menu icon" class="menu-icon w-5 h-5 object-contain" />`;
    iconSpan.classList.add("menu-icon", "w-5", "h-5", "object-contain");
    node.insertBefore(iconSpan, node.querySelector(".flex-1"));
  };

  const createChevron = () => {
    const chevron = doc.createElement("span");
    chevron.className = "chevron";
    chevron.setAttribute("data-toggle", "1");
    chevron.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    `;
    return chevron;
  };

  const createSingleMenuItem = (item) => {
    const node = cloneProto();
    injectIcon(node, item.image);
    node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
    node.setAttribute("data-link", item.link || "#");
    node.setAttribute("data-type", "single");

    if (current === normalize(item.link)) {
      node.classList.add("active");
    }

    return { node, hasActive: current === normalize(item.link) };
  };

  const createGroupMenuItem = (item, depth = 0) => {
    const node = cloneProto();
    injectIcon(node, item.image);
    node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
    node.setAttribute("data-link", "#");
    node.setAttribute("data-type", "group");
    node.classList.add("has_submenu");
    node.appendChild(createChevron());

    const groupId = "group-" + Math.random().toString(36).slice(2, 8);
    node.setAttribute("data-group-id", groupId);

    const submenuContainer = doc.createElement("div");
    submenuContainer.className = "submenu-container";
    submenuContainer.setAttribute("data-group-id", groupId);

    let hasActiveChild = false;

    item.submenu.forEach((sub) => {
      const result = createMenuItem(sub, depth + 1);
      submenuContainer.appendChild(result.node);
      if (result.hasActive) hasActiveChild = true;
    });

    if (hasActiveChild) {
      submenuContainer.classList.add("open");
      node.querySelector(".chevron")?.classList.add("rotate");
    }

    const wrapper = doc.createElement("div");
    wrapper.className = "mb-1";
    wrapper.appendChild(node);
    wrapper.appendChild(submenuContainer);

    return { node: wrapper, hasActive: hasActiveChild };
  };

  const createMenuItem = (item, depth = 0) => {
    const isGroup = Array.isArray(item.submenu) && item.submenu.length > 0;
    return isGroup ? createGroupMenuItem(item, depth) : createSingleMenuItem(item);
  };

  menus.forEach((menu) => {
    const result = createMenuItem(menu);
    sidebar.appendChild(result.node);
  });

  menuProto.remove();

  const style = `
    <style>
      .submenu-container {
        margin-top: 4px;
        margin-left: 16px;
        display: none;
      }
      .submenu-container.open {
        display: block;
      }
      .active {
        background: white !important;
        color: #0f766e !important;
      }
      #sidebar::-webkit-scrollbar {
        width: 6px;
      }
      #sidebar::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.3);
        border-radius: 10px;
      }
      .chevron.rotate svg {
        transform: rotate(180deg);
      }
    </style>
  `;

  const script = `
  <script>
    document.addEventListener("click", (e) => {
      const menuItem = e.target.closest(".menu-header");
      if (!menuItem) return;
 
      const type = menuItem.getAttribute("data-type");
 
      if (type === "group") {
        const groupId = menuItem.getAttribute("data-group-id");
        const container = document.querySelector('.submenu-container[data-group-id="' + groupId + '"]');
        if (container) {
          container.classList.toggle("open");
          menuItem.querySelector(".chevron")?.classList.toggle("rotate");
        }
        e.stopPropagation();
        return;
      }
 
      if (type === "single") {
        const link = menuItem.dataset.link;
        if (link && link !== "#") {
          document.querySelectorAll(".active").forEach(el => el.classList.remove("active"));
          menuItem.classList.add("active");
          window.parent.postMessage({ type: "NAVIGATE", link }, "*");
        }
        e.stopPropagation();
      }
    });
  </script>
`;

  return "<!doctype html>\n" + doc.documentElement.outerHTML.replace("</body>", style + script + "</body>");
};
